/**
 * The public home page.
 *
 * Rebuilt from the design artboard rather than grown out of the page that was
 * here before. Nine bands, in the design's order:
 *
 *   1  the claim, with the app translating beside it
 *   2  install the app, or add it to your browser
 *   3  what are you trying to do          — one preset featured, four beside it
 *   4  what it does during a call
 *   5  what we carry for you — and what stays yours
 *   6  your audio does not route through us
 *   7  Linux first — and we will tell you where it is rough
 *   8  why it works this way
 *   9  the claim again
 *
 * The scale is the design's, held in --site-* tokens rather than the
 * dashboard's ramp; see Landing.scss. What the repeated sections are made of
 * lives in homeSections.ts, so a test can check the locale keys they imply.
 */

import { Link } from 'react-router-dom';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import { useMetaDescription } from '@/lib/document-meta';
import { AppDemo } from '@/components/site/AppDemo';
import { FlowChain } from '@/components/site/FlowChain';
import { RoutingDiagram } from '@/components/site/RoutingDiagram';
import { SCENARIOS, titleKey, bodyKey } from '@/components/site/scenarios';
import { LANGUAGE_NAME, counterpartLocale } from '@/components/site/demoScript';
import {
  CARRY_OURS,
  CARRY_YOURS,
  FAQ,
  FEATURES,
  MEETING_SITES,
  PLATFORMS,
  STATS,
} from '@/components/site/homeSections';
import './Landing.scss';

const REPO = 'github.com/kizuna-ai-lab/sokuji';

export function Landing() {
  const { t, locale } = useI18n();
  const lp = useLocalizedPath();
  useMetaDescription(t('landing.subtitle'));

  // The pair the hero panel is speaking, so the page never tells a visitor two
  // different stories about which languages are in play.
  const mine = LANGUAGE_NAME[locale];
  const theirs = LANGUAGE_NAME[counterpartLocale(locale)];

  const featured = SCENARIOS.find((s) => s.id === 'be-heard')!;
  const rest = SCENARIOS.filter((s) => s.id !== 'be-heard');

  return (
    <div className="landing">
      {/* 1 ─ the claim ─────────────────────────────────────────────────── */}
      <section className="landing__hero">
        {/* The break is the pivot the sentence turns on, so it is markup and
            not a wrap the viewport happens to choose. */}
        <h1 className="landing__hero-title">
          {t('landing.slogan.line1')}
          <br />
          {t('landing.slogan.line2')}
        </h1>

        <div className="landing__hero-body">
          <div className="landing__hero-copy">
            <p className="landing__hero-sub">{t('landing.subtitle')}</p>
            <div className="landing__cta-row">
              <Link to={lp('/download')} className="landing__cta landing__cta--primary">
                {t('landing.cta.desktop')}
              </Link>
              <Link to={lp('/download#extension')} className="landing__cta landing__cta--secondary">
                {t('landing.cta.extension')}
              </Link>
            </div>
            <div className="site-label landing__hero-trust">{t('landing.trust')}</div>
          </div>

          <div className="landing__hero-demo">
            <AppDemo />
          </div>
        </div>
      </section>

      {/* 2 ─ install ───────────────────────────────────────────────────── */}
      <section className="landing__band">
        <h2>{t('home.install.title')}</h2>
        <p className="landing__lead">{t('home.install.lead')}</p>

        <div className="landing__install">
          <article className="landing-card landing-card--roomy landing-card--prose">
            <h3 className="landing-card__title">{t('home.install.desktop.title')}</h3>
            <p className="landing-card__body">{t('home.install.desktop.body')}</p>
            <p className="landing-card__body">{t('home.install.desktop.note')}</p>
            <p className="landing__platform-routing site-label">
              {`Linux  /  ${t('home.install.routing.linux')}`}
              <br />
              {`macOS  /  ${t('home.install.routing.macos')}` +
                `    ·    Windows  /  ${t('home.install.routing.windows')}`}
            </p>
          </article>

          <article className="landing-card landing-card--roomy landing-card--prose">
            <h3 className="landing-card__title">{t('home.install.ext.title')}</h3>
            <p className="landing-card__body">{t('home.install.ext.body')}</p>
            {/* Names, not a sentence: the question this answers is "is my
                meeting site on the list", which a list answers faster. */}
            <p className="landing__sites site-label">{MEETING_SITES.join(' · ')}</p>
            <p className="landing-card__body">{t('home.install.ext.note')}</p>
          </article>
        </div>
      </section>

      {/* 3 ─ what are you trying to do ─────────────────────────────────── */}
      <section className="landing__band">
        <h2>{t('home.scenarios.title')}</h2>
        <p className="landing__lead">{t('home.scenarios.lead')}</p>

        {/* The one we built first carries the diagram: it is the only preset
            where the other side needs nothing, which is the whole argument and
            is far easier to see than to read. */}
        <article className="landing-card landing__featured">
          <div className="landing__featured-copy">
            <div className="site-label landing__featured-kicker">
              {t('home.scenarios.featured')}
            </div>
            <h3 className="landing__featured-title">{t(titleKey(featured.id))}</h3>
            <p className="landing-card__body">{t(bodyKey(featured.id))}</p>
          </div>
          <div className="landing__featured-figure">
            <FlowChain
              label={t('home.install.flowLabel')}
              nodes={[
                { kind: 'person', label: t('flow.youSpeak') },
                { kind: 'sokuji', label: t('flow.translates') },
                { kind: 'mic', label: t('flow.virtualMic') },
                { kind: 'app', label: t('flow.anyApp') },
                { kind: 'person', label: t('flow.theyHear') },
              ]}
              hops={[
                { label: mine, translated: false },
                { label: theirs, translated: true },
                { translated: true },
                { translated: true },
              ]}
            />
          </div>
        </article>

        <div className="landing-grid landing-grid--2">
          {rest.map((s) => (
            <article className="landing-card" key={s.id}>
              <h3 className="landing-card__title landing-card__title--sm">{t(titleKey(s.id))}</h3>
              <p className="landing-card__body">{t(bodyKey(s.id))}</p>
              <div className="site-label landing-card__foot">{t(`scenario.${s.id}.flow`)}</div>
            </article>
          ))}
        </div>
      </section>

      {/* 4 ─ what it does during a call ────────────────────────────────── */}
      <section className="landing__band" id="features">
        <h2>{t('home.during.title')}</h2>
        <p className="landing__lead">{t('home.during.lead')}</p>

        <div className="landing-grid landing-grid--3">
          {FEATURES.map((k) => (
            <article
              key={k}
              className={
                'landing-card' +
                // The voice card carries the longest explanation and the
                // offline one is the argument the whole site rests on; the
                // grid gives them the room rather than cropping them to match.
                (k === 'voice' ? ' landing-span-2' : '') +
                (k === 'offline' ? ' landing-span-3 landing-card--dark' : '')
              }
            >
              <h3 className="landing-card__title">{t(`feat.${k}.t`)}</h3>
              <p className="landing-card__body">{t(`feat.${k}.b`)}</p>
            </article>
          ))}
        </div>

        <p className="landing__after landing__after--wide">{t('home.during.also')}</p>
      </section>

      {/* 5 ─ what we carry ─────────────────────────────────────────────── */}
      <section className="landing__band">
        <h2>{t('home.carry.title')}</h2>
        <p className="landing__lead">{t('home.carry.lead')}</p>

        {/* Two panels, not eight cards: each convenience has an exit directly
            across from it, and that pairing is the point. */}
        <div className="landing-grid landing-grid--2">
          <article className="landing-card landing-card--roomy">
            <div className="site-label landing__carry-label">{t('carry.ours')}</div>
            <div className="landing__carry-list">
              {CARRY_OURS.map((k) => (
                <div key={k}>
                  <h3 className="landing-card__title landing-card__title--sm">
                    {t(`carry.${k}.t`)}
                  </h3>
                  <p className="landing-card__body">{t(`carry.${k}.b`)}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="landing-card landing-card--roomy landing-card--dark">
            <div className="site-label landing__carry-label landing__carry-label--accent">
              {t('carry.yours')}
            </div>
            <div className="landing__carry-list">
              {CARRY_YOURS.map((k) => (
                <div key={k}>
                  <h3 className="landing-card__title landing-card__title--sm">
                    {t(`carry.${k}.t`)}
                  </h3>
                  <p className="landing-card__body">{t(`carry.${k}.b`)}</p>
                </div>
              ))}
            </div>
          </article>
        </div>

        <div className="landing__stats">
          {STATS.map(([figure, key]) => (
            <div key={key}>
              <div className="landing__stat-figure">{figure}</div>
              <div className="site-label landing__stat-label">{t(key)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 6 ─ where the audio goes ──────────────────────────────────────── */}
      <section className="landing__band">
        <article className="landing-card landing__routing">
          <h2>{t('home.routing.title')}</h2>
          <p className="landing__lead">{t('home.routing.lead')}</p>
          <div className="landing__routing-figure">
            <RoutingDiagram
              label={t('routing.label')}
              you={t('routing.you')}
              sokuji={t('routing.sokuji')}
              provider={t('routing.provider')}
              providerNote={t('routing.providerNote')}
              up={t('routing.up')}
              down={t('routing.down')}
              youSpeak={t('flow.youSpeak')}
              theyHear={t('flow.theyHear')}
              mic={t('routing.mic')}
              app={t('routing.app')}
              them={t('routing.them')}
              kizuna={t('routing.kizuna')}
              kizunaCarries={t('routing.kizunaCarries')}
              or={t('routing.or')}
            />
          </div>
        </article>
      </section>

      {/* 7 ─ platforms ─────────────────────────────────────────────────── */}
      <section className="landing__band" id="platforms">
        <h2 className="landing__h2--alone">{t('home.platforms.title')}</h2>

        <div className="landing-grid landing-grid--3">
          {PLATFORMS.map((os) => (
            <article key={os} className="landing-card landing-card--footed">
              <h3 className="landing-card__title landing-card__title--os">
                {t(`download.${os}.title`)}
              </h3>
              <p className="landing-card__body">{t(`home.platform.${os}.body`)}</p>
              {/* One label on all three, and the same distance from the bottom
                  of the card whatever length the paragraph above it runs to.
                  Each lands on that platform's builds rather than handing you
                  one we picked — an Intel Mac and an arm64 laptop are each
                  somebody's only machine. */}
              <Link to={lp(`/download#${os}`)} className="landing__download">
                {t('home.platform.cta')} &rarr;
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* 8 ─ why it works this way ─────────────────────────────────────── */}
      <section className="landing__band">
        <h2>{t('home.faq.title')}</h2>
        <p className="landing__lead">{t('home.faq.lead')}</p>

        <div className="landing-grid landing-grid--2">
          {FAQ.map((k) => (
            <article className="landing-card landing-card--prose" key={k}>
              <h3 className="landing-card__title landing-card__title--q">{t(`faq.${k}.q`)}</h3>
              <p className="landing-card__body">
                {/* The repo URL is the load-bearing part of the "why believe
                    you" answer, so it is a link and not grey prose. */}
                {t(`faq.${k}.a`)
                  .split(REPO)
                  .flatMap((part, i) =>
                    i === 0
                      ? [part]
                      : [
                          <a
                            key={i}
                            href={`https://${REPO}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {REPO}
                          </a>,
                          part,
                        ],
                  )}
              </p>
            </article>
          ))}
        </div>
      </section>

    </div>
  );
}
